'use client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Link from 'next/link';
import { UseFormReturn } from 'react-hook-form';

import TextArea from '../Textarea';
import Button from '../Button';
import LoginModal from '../LoginModal';
import DatePicker from '../DatePicker';
import CountrySelect from '../CountrySelect';
import Input from '../Input';
import QuantitieInputRoot from '../QuantitieInput/Root';
import QuantitieInputBox from '../QuantitieInput/Box';

import useCountries from '@/hooks/useCountries';
import { getSelectDescription } from '../ContactForm/helpers';
import useVillageContext from '@/hooks/useVillageContext';
import useUserContext from '@/hooks/useUserContext';
import {
  SendVillageEnquiryRequest,
  sendVillageEnquiry,
} from '@/services/village';
import { bookSideWidgetInfosDefaultValues } from '@/contexts/villageContext';
import { cn } from '@/lib/utils';

import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import { Village } from '@/services/types';

export type VillageEnquiryFormValues = {
  name: string;
  surname: string;
  country_info: {
    name: string;
    code: string;
    id: number;
    phone: {
      code: number;
      country: string;
    };
  };
  email: string;
  phone: string;
  dates: {
    start: Date | null;
    end: Date | null;
  };
  adults: number;
  children: number;
  infants: number;
  flexible?: boolean;
  terms: boolean;
  note?: string;
};

type Props = {
  village: Village;
  form: UseFormReturn<VillageEnquiryFormValues>;
  formDict: IEnquiryFormDictionary;
  lang: string;
  countriesDict: ICountriesDictionary;
  modalsDict: IModalsDictionary;
  datePickerDict: IDatePickerDictonary;
  setSuccessModalIsOpen: Dispatch<SetStateAction<boolean>>;
  atModal?: boolean;
  onSuccess?: () => void;
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function VillageEnquiryForm({
  village,
  form,
  formDict,
  lang,
  countriesDict,
  modalsDict,
  datePickerDict,
  atModal,
  setSuccessModalIsOpen,
  onSuccess,
}: Props) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showDatesError, setShowDatesError] = useState(false);
  const [blockSubmit, setBlockSubmit] = useState(false);

  const { customer, user, isLogged } = useUserContext();
  const { bookSideWidgetInfos, setBookSideWidgetInfos } = useVillageContext();
  const { optionedDates } = useDatePickerContext();

  const defaultDates = {
    start: bookSideWidgetInfos.start,
    end: bookSideWidgetInfos.end,
  };

  const { countries } = getSelectDescription(lang, countriesDict);

  const { translatedCountries, languageCountry } = useCountries({
    lang,
    countriesDict,
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = form;

  const dates = watch('dates');

  function handleSetCountry(code: string, name: string, id: number) {
    setValue('country_info.name', name);
    setValue('country_info.code', code);
    setValue('country_info.id', id);
    const foundCountry = translatedCountries.find(
      (country) => country.name === name,
    );

    if (foundCountry) {
      setValue('country_info.phone.code', foundCountry.id);
      setValue('country_info.phone.country', foundCountry.name);
    }
  }

  const onSubmitClick = () => {
    if (atModal && optionedDates) {
      return;
    }
    setShowDatesError(false);
    if (!dates || dates?.start === null || dates?.end === null) {
      setShowDatesError(true);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (blockSubmit) return;
    setBlockSubmit(true);

    window.dataLayer = window.dataLayer || [];

    const moment = (await import('moment')).default;

    let timestampStart = moment(data.dates.start).format('YYYY-MM-DD');
    let timestampEnd = moment(data.dates.end).format('YYYY-MM-DD');

    if (atModal && optionedDates) {
      timestampStart = optionedDates.timestampStart;
      timestampEnd = optionedDates.timestampEnd;
    }

    const flexibleDates = data.flexible ?? false;
    const note = data.note ?? '';

    const body: SendVillageEnquiryRequest = {
      villa: village.id.toString(),
      experience: null,
      timestampStart,
      timestampEnd,
      customer: null,
      title: null,
      firstName: data.name,
      lastName: data.surname,
      country: data.country_info.id,
      email: data.email,
      phoneCode: data.country_info.id,
      phone: data.phone.toString(),
      adults: data.adults,
      children: data.children,
      infants: data.infants,
      flexibleDates,
      note,
      availabilityNotification: false,
      privacyPolicy: data.terms,
      language: lang,
    };

    try {
      const response = await sendVillageEnquiry(body);
      if (response.data) {
        setSuccessModalIsOpen(true);
        reset();
        setBlockSubmit(false);

        window.dataLayer.push({
          event: 'eventInfoVilla',
          eventCategory: 'send_info_villa',
          eventAction: 'form_villa',
          eventLabel: `${village.title} | ${village.id}`,
          language: lang,
        });

        if (onSuccess) {
          onSuccess();
        }
      } else {
        setBlockSubmit(false);
      }
    } catch (error) {
      console.log(error);
    }
  });

  function handleConfirmDates(value: { start: Date | null; end: Date | null }) {
    setShowDatesError(false);
    setValue('dates', value);

    setBookSideWidgetInfos((prev) => ({
      ...prev,
      start: value.start,
      end: value.end,
    }));
  }

  function handleQuantitiesOnChange({
    field,
    value,
  }: {
    field: string;
    value: number;
  }) {
    setValue(field as 'adults' | 'children' | 'infants', value);

    setBookSideWidgetInfos((prev) => ({
      ...prev,
      quantities: {
        ...prev.quantities,
        [field]: value,
      },
    }));
  }

  useEffect(() => {
    const { start, end } = bookSideWidgetInfos;
    if (start != null && end !== null) {
      setBookSideWidgetInfos(bookSideWidgetInfosDefaultValues);
    }
    // eslint-disable-next-line
  }, [village]);

  useEffect(() => {
    const { name, code, id } = getSelectDescription(lang, countriesDict);

    if (isLogged) {
      const country = countries.find(
        (country) => country.id == customer?.country?.id,
      );
      const countryPhone = countries.find(
        (country) => country.id == customer?.phoneCode?.id,
      );

      const enquiryDefault = {
        name: user.firstName,
        surname: user.lastName,
        email: user.emailCanonical,
        phone: customer.phone ? customer.phone : '',
        country_info: {
          name: country ? country?.name : name,
          code: country ? country?.code : code,
          id: country ? country?.id : id,
          phone: {
            code: countryPhone ? countryPhone?.id : id,
            country: countryPhone ? countryPhone?.name : name,
          },
        },
        adults: 0,
        children: 0,
        infants: 0,
      };
      reset(enquiryDefault);
    } else {
      const enquiryDefault = {
        country_info: {
          name,
          code,
          id,
          phone: {
            code: id,
            country: name,
          },
        },
        adults: 0,
        children: 0,
        infants: 0,
      };
      reset(enquiryDefault);
    }
    // eslint-disable-next-line
  }, [isLogged]);

  return (
    <form
      id="village-enquiry-form"
      className={cn(
        'flex flex-col gap-4 desktop:shadow-default w-full desktop:w-[110%] desktop:py-[60px] desktop:pr-[60px] desktop:pl-[15%] desktop:-ml-[10%] mt-5 desktop:mt-0',
        {
          'desktop:shadow-none desktop:w-full desktop:py-0 desktop:pr-0 desktop:pl-0 desktop:ml-0 mt-0':
            atModal,
        },
      )}
      onSubmit={onSubmit}>
      {!isLogged && (
        <div className="flex font-averta-semibold gap-2 py-4">
          <p className="text-lg">{formDict.loginLabel}</p>
          <LoginModal lang={lang} dict={modalsDict} text />
        </div>
      )}
      <div className="flex flex-col desktop:flex-row gap-8">
        <Input.Container className="desktop:w-1/2">
          <Input.Label htmlFor="name">{formDict.name}</Input.Label>
          <Input.Root
            register={register}
            name="name"
            disabled={isLogged}
            className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
          />
          <Input.Error error={errors.name} />
        </Input.Container>
        <Input.Container className="desktop:w-1/2">
          <Input.Label htmlFor="surname">{formDict.surname}</Input.Label>
          <Input.Root
            register={register}
            name="surname"
            disabled={isLogged}
            className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
          />
          <Input.Error error={errors.surname} />
        </Input.Container>
      </div>
      <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-8">
        <div className="w-full desktop:w-[calc(25%-1.875rem)]">
          <CountrySelect
            countries={countries}
            label={formDict.country}
            id={'country-selector'}
            onChange={({ code, name, id }) => handleSetCountry(code, name, id)}
            selectedValue={countries.find(
              (option) => option.name === watch('country_info.name'),
            )}
          />
        </div>
        <Input.Container className="xl:w-[calc(41.66667%-1.875rem)] desktop:w-[calc(33.33333%-1.875rem)]">
          <Input.Label htmlFor="email">{formDict.email}</Input.Label>
          <Input.Root
            register={register}
            name="email"
            disabled={isLogged}
            className={isLogged ? 'opacity-70 cursor-not-allowed' : ''}
          />
          <Input.Error error={errors.email} />
        </Input.Container>
        <div className="flex flex-col flex-1">
          <label
            className="text-sm text-gray-700 leading-[1.8]"
            htmlFor="phone-country-selector">
            {formDict.phone}
          </label>
          <div className="flex flex-row">
            <div className="min-w-[140px] desktop:min-w-[120px] h-fit">
              <CountrySelect
                countries={countries}
                label=""
                id={'phone-country-selector'}
                showPhoneCode
                onChange={({ id, name }) => {
                  setValue('country_info.phone.code', id);
                  setValue('country_info.phone.country', name);
                }}
                selectedValue={countries.find(
                  (option) =>
                    option.name === watch('country_info.phone.country'),
                )}
                disabled={customer.phoneCode ? true : false}
                buttonClassName={
                  customer.phoneCode ? 'opacity-70 cursor-not-allowed' : ''
                }
              />
            </div>
            <Input.Container className="w-full">
              <Input.Root
                id="phone-country-selector-content"
                aria-label="Phone input field"
                register={register}
                name="phone"
                disabled={customer.phone ? true : false}
                className={
                  customer.phone ? 'opacity-70 cursor-not-allowed' : ''
                }
              />
              <Input.Error error={errors.phone} />
            </Input.Container>
          </div>
        </div>
      </div>

      <>
        <div className="flex flex-wrap desktop:justify-between">
          <div className="w-full desktop:w-[calc(50%-1rem)] desktop:mr-[0.9375rem]">
            <DatePicker.Root open={modalIsOpen} onOpenChange={setModalIsOpen}>
              <DatePicker.Trigger
                dict={datePickerDict}
                confirmedDates={defaultDates}
                id="enquiry-datepicker-trigger"
              />
              <DatePicker.Content
                lang={lang}
                confirmedDates={defaultDates}
                village={village}
                skipCheckInDaysValidation
                confirmDates={handleConfirmDates}
                onOpenChange={setModalIsOpen}
                dict={datePickerDict}
              />
            </DatePicker.Root>
          </div>
          <div className="w-full flex-[0_0_auto] flex mt-4 md:w-[90%] lg:w-4/5 desktop:w-[calc(50%-1rem)] desktop:mt-0 desktop:ml-[0.9375rem]">
            <QuantitieInputRoot
              className="w-full"
              id="adults"
              label={formDict.adults}>
              <QuantitieInputBox
                onChange={(value) =>
                  handleQuantitiesOnChange({ field: 'adults', value })
                }
                value={bookSideWidgetInfos.quantities.adults}
                max={1001}
              />
            </QuantitieInputRoot>
            <QuantitieInputRoot
              className="w-full"
              id="childrens"
              label={formDict.children}>
              <QuantitieInputBox
                onChange={(value) =>
                  handleQuantitiesOnChange({ field: 'children', value })
                }
                value={bookSideWidgetInfos.quantities.children}
                max={1002}
              />
            </QuantitieInputRoot>
            <QuantitieInputRoot
              className="w-full"
              id="infants"
              label={formDict.infants}>
              <QuantitieInputBox
                onChange={(value) =>
                  handleQuantitiesOnChange({ field: 'infants', value })
                }
                value={bookSideWidgetInfos.quantities.infants}
                max={1003}
              />
            </QuantitieInputRoot>
          </div>
        </div>
        {showDatesError && (
          <div className="flex flex-col">
            <i className="text-error-primary font-averta-italic text-xs">
              {formDict.errors.dates.checkIn}
            </i>
            <i className="text-error-primary font-averta-italic text-xs">
              {formDict.errors.dates.checkOut}
            </i>
          </div>
        )}
      </>
      {!atModal && (
        <div className="flex flex-col">
          <div className="flex items-start gap-2 md:w-[20rem] lg:w-[30rem] desktop:w-full">
            <input
              className={cn('checkbox-input', {
                'border border-underline': watch('flexible') == true,
              })}
              {...register('flexible')}
              onChange={(e) => setValue('flexible', e.target.checked)}
              type="checkbox"
              name="flexible-checkbox"
              id="flexible-checkbox"
            />
            <label
              className="text-sm text-footer-gray mb-5"
              htmlFor="flexible-checkbox">
              {formDict.flexible}
            </label>
          </div>
        </div>
      )}
      <div className="flex flex-col">
        <div className="flex items-start gap-2 md:w-[20rem] lg:w-[30rem] desktop:w-full">
          <input
            className={cn('checkbox-input', {
              'border border-underline': watch('terms') == true,
            })}
            {...register('terms')}
            onChange={(e) => setValue('terms', e.target.checked)}
            type="checkbox"
            name="checkbox-input"
            id="enquiry-checkbox-input"
          />
          <label
            className="text-sm text-footer-gray"
            htmlFor="enquiry-checkbox-input">
            {formDict.terms}
            <Link href={formDict.privacyHref} target="_blank">
              <span className="hover:text-gray-900 underline cursor-pointer ml-1 text-footer-gray">
                {formDict.privacyPolicy}
              </span>
            </Link>
          </label>
        </div>
        <Input.Error error={errors.terms} />
      </div>
      {!atModal && (
        <TextArea
          className="mt-4"
          register={register}
          name="note"
          label={formDict.note}
          error={errors.note}
        />
      )}
      <div className="flex justify-start tablet:justify-end">
        <Button
          onClick={onSubmitClick}
          type="submit"
          className={cn('w-auto px-[30px]', {
            'mb-4 desktop:mb-0': atModal,
            'hover:bg-primary hover:text-white': blockSubmit,
          })}
          disabled={blockSubmit}>
          {formDict.sendEnquiry}
        </Button>
      </div>
    </form>
  );
}
